import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { useMemo, useState } from "react";
import { PageLayout, PageSection } from "@/components/site/PageLayout";
import { Reveal } from "@/components/site/Reveal";
import type { BlogPost } from "@/lib/blog";
import { listBlogPosts } from "@/lib/blog.functions";

export const Route = createFileRoute("/blog/")({
  loader: () => listBlogPosts(),

  head: () => ({
    ...pageHead({
      title: "Solar Engineering Insights · SunSmart Engineering",
      description:
        "Engineering insights on solar PV, energy storage, pools, structural design and the permitting process from the SunSmart Engineering team.",
      path: "/blog",
    }),
  }),
  component: BlogIndex,
});

const PAGE_SIZE = 12;

function formatDate(iso: string): string {
  const time = Date.parse(iso);
  if (Number.isNaN(time)) return "";
  return new Date(time).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function PostCard({ post, large = false }: { post: BlogPost; large?: boolean }) {
  return (
    <Link
      to="/blog/$slug"
      params={{ slug: post.slug }}
      className="group flex h-full flex-col overflow-hidden rounded-sm border border-border bg-surface transition-colors hover:border-sun/60"
    >
      <div className={`relative overflow-hidden bg-surface-2 ${large ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
        {post.main_image ? (
          <img
            src={post.main_image}
            alt={post.title}
            loading="lazy"
            decoding="async"
            width={800}
            height={600}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="h-full w-full bg-surface-2" />
        )}
        {post.video_url && (
          <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5 rounded-sm border border-sun/60 bg-background/85 px-2 py-1 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-sun backdrop-blur-sm">
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
            Video
          </div>
        )}
      </div>
      <div className={`flex flex-1 flex-col bg-background/95 ${large ? "p-8 md:p-10" : "p-6"}`}>
        <div className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-sun">
          {post.type} {post.created_on && <span className="text-muted-foreground">· {formatDate(post.created_on)}</span>}
        </div>
        <h3
          className={`font-display font-black uppercase leading-tight text-balance ${
            large ? "text-2xl md:text-4xl" : "text-lg md:text-xl line-clamp-3"
          }`}
        >
          {post.title}
        </h3>
        <p
          className={`mt-3 text-sm leading-relaxed text-muted-foreground ${
            large ? "max-w-2xl" : "line-clamp-3"
          }`}
        >
          {post.summary}
        </p>
        <div className="mt-5 inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-sun">
          Read more <span className="transition-transform group-hover:translate-x-1">→</span>
        </div>
      </div>
    </Link>
  );
}

function BlogIndex() {
  const posts = Route.useLoaderData() as BlogPost[];
  const [category, setCategory] = useState<string>("All");
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const categories = useMemo(() => {
    const set = new Set<string>();
    posts.forEach((p) => p.type && set.add(p.type));
    return ["All", ...Array.from(set).sort()];
  }, [posts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      if (category !== "All" && p.type !== category) return false;
      if (!q) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        p.summary.toLowerCase().includes(q)
      );
    });
  }, [posts, category, query]);

  const [featured, ...rest] = filtered;
  const shown = rest.slice(0, Math.max(visible - 1, 0));
  const hasMore = shown.length < rest.length;

  return (
    <PageLayout
      kicker="Insights · Engineering Notes"
      title="Insights"
      intro="Field notes and technical breakdowns from the SunSmart Engineering team, covering solar PV, energy storage, pools, structural design, and everything it takes to get a project permitted."
    >
      <PageSection label="01" title="Latest">
        <Reveal>
          <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-3">
              {categories.map((c) => {
                const active = c === category;
                return (
                  <button
                    key={c}
                    onClick={() => {
                      setCategory(c);
                      setVisible(PAGE_SIZE);
                    }}
                    className={`rounded-full border px-4 py-1.5 font-mono text-[0.7rem] uppercase tracking-[0.22em] transition-colors ${
                      active
                        ? "border-sun text-sun"
                        : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
            <label className="relative block w-full max-w-sm">
              <span className="sr-only">Search articles</span>
              <input
                type="search"
                placeholder="Search insights…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setVisible(PAGE_SIZE);
                }}
                className="w-full rounded-full border border-border bg-background/40 px-5 py-2.5 font-mono text-[0.75rem] uppercase tracking-[0.18em] text-foreground placeholder:text-muted-foreground focus:border-sun focus:outline-none"
              />
            </label>
          </div>
        </Reveal>

        {featured && (
          <Reveal>
            <PostCard post={featured} large />
          </Reveal>
        )}

        {shown.length > 0 && (
          <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((post, i) => (
              <Reveal key={post.slug} delay={(i % 6) * 80}>
                <PostCard post={post} />
              </Reveal>
            ))}
          </div>
        )}

        {filtered.length === 0 && (
          <div className="mt-14 text-center font-mono text-[0.7rem] uppercase tracking-[0.22em] text-muted-foreground">
            No posts match your filters.
          </div>
        )}

        {hasMore && (
          <div className="mt-14 flex justify-center">
            <button
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background/30 px-7 py-4 text-sm font-semibold text-foreground backdrop-blur hover:bg-surface transition-colors"
            >
              Load more <span aria-hidden>→</span>
            </button>
          </div>
        )}

        {!hasMore && rest.length > 0 && (
          <div className="mt-14 text-center font-mono text-[0.7rem] uppercase tracking-[0.22em] text-muted-foreground">
            <span className="text-sun">{filtered.length}</span> {filtered.length === 1 ? "post" : "posts"} · You've reached the end
          </div>
        )}
      </PageSection>
    </PageLayout>
  );
}
